import { onlineManager } from '@tanstack/react-query';
import { getErrorStatus } from '@utils/errorDetection';

/**
 * Did this attempt end in an error, and with what?
 *
 * v5's `refetch()` RESOLVES with the settled observer result rather than rejecting (it only
 * rejects under `throwOnError`), so "the attempt failed" is a property of the value, not a
 * rejection. Read structurally so this module can keep taking a plain `() => unknown`: a
 * screen hands it its own query's `refetch` (or `fetchNextPage`, which resolves with the same
 * result) and nothing else. A caller that returns something else (a wrapper or a test double
 * returning nothing) reads as "outcome unknown", and the belief correction
 * below is then simply left in place: leaving the flag online costs a reconnect-resume, leaving
 * it wrongly offline would freeze every query in the app, so unknown resolves toward the
 * cheaper mistake.
 *
 * Narrowed rather than boolean so the caller can reach `result.error` without a second cast:
 * the failure's HTTP status is what decides whether the belief may be put back.
 */
function attemptFailed(result: unknown): result is { isError: true; error?: unknown } {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as { isError?: unknown }).isError === true
  );
}

/**
 * What a retry control does for a read that failed or is parked.
 *
 * The rule: a control shown for a read that failed or is parked, whose tap reads that query
 * again ("Try again", "Retry"), calls this, and hands it that query's own `refetch`, or its
 * `fetchNextPage` when what failed was the next page. A control that reads a feed again for
 * another reason is outside the rule and does not call this: pull-to-refresh and the new-posts
 * pill refresh a feed that loaded, through a `refresh` that resolves at once when the read is
 * parked. It does not call the query's function itself, for the reason below. And it
 * does not hand over a wrapper that resolves with nothing: the outcome of the attempt is read
 * off what the function resolves with (see `attemptFailed`), and with no outcome the belief
 * corrected here is never put back. A control stays out of it when no query read stands behind
 * its tap: a query under networkMode 'always' is never parked, and a page reload reads nothing
 * through the library. A control that runs a MUTATION again is out of it for another reason:
 * the online manager parks mutations exactly as it parks queries, but this takes a query's
 * refetch and reads a query's result, so it has nothing to hand a mutation.
 *
 * "Try again" has to mean try again, including when the app believes it is offline.
 *
 * Under networkMode 'online' (the repo-wide default) a bare refetch() while onlineManager
 * reports offline fails the retryer's canStart() and parks the query again: no request is
 * sent. A query with nothing loaded also goes back to pending with no error, so a screen that
 * was showing its error state falls through to whatever it shows for no data, which can be an
 * empty state that is false; a query that still has data keeps its error and its button, and
 * the button does nothing. Either way it reads as broken. And the belief can simply
 * be STALE. `#online` is process-lifetime state that v5 flips from window 'online'/'offline'
 * events and never re-derives from navigator.onLine, so it stays false if the 'offline' event
 * was delivered and the matching 'online' event was not. A native WebView suspended in the
 * background is the concrete case: the page is frozen across the transition and the 'online'
 * that fired while it slept is not replayed on resume, so navigator.onLine reads true while
 * onlineManager still says false. So correct the belief first, then fetch. `setOnline(true)`
 * is a no-op when it already believes online, which is why there is no branch here.
 *
 * What the press actually does, in both worlds:
 *  - connectivity is fine (stale belief): the request goes out and succeeds, and the screen it
 *    was blocking resolves. Correcting the flag also unparks anything else the manager had
 *    frozen, exactly what a real 'online' event would have done.
 *  - genuinely offline: the request goes out and FAILS. The query settles on `error` (its own
 *    retry policy bounds the attempt, then stops: this adds no loop of its own and no timer),
 *    classifyLoadError's status-less + !navigator.onLine fallback returns 'offline', and the
 *    person gets the same screen back. Honest, and still one attempt per press.
 *
 * That holds while no WRITE is parked. With a mutation paused (sent while the app believed it
 * was offline), `setOnline(true)` wakes QueryClient's own subscriber, which first resumes the
 * paused mutations and waits for them to settle, their retries included, and only then
 * restarts the paused queries. A read that was itself parked therefore starts late (about
 * seven seconds of backoff when the device really is offline), and a parked write that fails
 * there has failed for good: more than one attempt, and more than this read, per press. The
 * redesign is tracked with TECH-692.
 *
 * ...and then the belief is PUT BACK, if and only if the press itself changed it (the app
 * believed it was offline BEFORE the press), the attempt failed with a STATUS-LESS error, AND
 * the browser still reports offline. Without that restore the press silently disables app-wide
 * reconnect recovery for the rest of the outage: setOnline() notifies its listeners only when
 * the value CHANGES (query-core's OnlineManager.setOnline: `const changed = this.#online !==
 * online`), so a flag forced true while the device is really offline means (a) nothing parks
 * any more, every query in the app fires and hard-errors instead, and (b) the real 'online'
 * event at reconnect is not a change, so QueryClient's subscriber never runs
 * resumePausedMutations() or queryCache.onOnline(): refetchOnReconnect is dead for that
 * transition, and nothing restores it. Materially worse on native, where no page reload resets
 * the module singleton.
 *
 * FOUR conditions are required, and the restore only ever undoes this press's own write:
 *  - the app believed it was offline before the press. The press puts back only what it
 *    changed: when the app already believed it was online, `setOnline(true)` wrote nothing, so
 *    there is nothing to put back and the restore returns at once. This is the conjunct that
 *    keeps a connected device from being parked. navigator.onLine has a false negative on some
 *    native WebViews (Capacitor Android: classifyLoadError documents exactly this): it reads
 *    false while requests reach the server, and the app rightly believes it is online, because
 *    no 'offline' event was ever heard. Let one read there fail with no status (a timeout, a
 *    gateway error without CORS headers) and every other condition below holds on a perfectly
 *    connected device. Without this one the press would move the belief to offline, park every
 *    query and mutation in the app (networkMode 'online', no repo-wide override) behind an
 *    outage that is not happening, and no 'online' event would ever come to release them,
 *    because the device never left the network. What is given up, knowingly: a device that
 *    really is offline while the app still believed it was online is not parked by the press.
 *    The library's own 'offline' event does that.
 *  - keyed on navigator.onLine, so a restore can only ever leave the flag in a state the
 *    browser itself is already in. That guarantees a real reconnect is still a CHANGE, and
 *    therefore still fires.
 *  - gated on the attempt having failed, because a fetch that SUCCEEDED proves the device is
 *    online whatever navigator.onLine claims, so the corrected flag stays.
 *  - gated on the failure being STATUS-LESS, mirroring classifyLoadError's precedence
 *    (errorDetection.ts): if the server answered, the device was online for that request, and
 *    the status beats the connectivity signal. On a WebView with the false negative above, a
 *    belief that was stale (offline) before the press is corrected for good by any answer that
 *    carries a status, a 500 included.
 * Both completion paths are inspected the same way: v5 resolves with the settled result (so
 * the error is `result.error`), but a caller under `throwOnError`, or any refetch that throws
 * outright, rejects instead, and there the reason IS the error. A rejection handler that
 * skipped the status check would reopen the same hole on that path. The belief before the press
 * is read in the statement before the write that corrects it, and the restore's own read and
 * write are adjacent synchronous statements, so no 'online' event can interleave in either pair.
 *
 * Pinned against the real library in useWelcomeErrorHandlers.test.tsx (the belief and its
 * restore, case by case) and in tryLoadAgain.test.tsx (a real read behind a real button).
 */
export const tryLoadAgain = (refetch: () => unknown): void => {
  const believedOffline = !onlineManager.isOnline();
  onlineManager.setOnline(true);
  const restoreIfStatusLessAndBrowserOffline = (error: unknown) => {
    if (!believedOffline) return;
    if (getErrorStatus(error) !== undefined) return;
    if (!navigator.onLine) onlineManager.setOnline(false);
  };
  void Promise.resolve(refetch()).then(result => {
    if (attemptFailed(result)) restoreIfStatusLessAndBrowserOffline(result.error);
  }, restoreIfStatusLessAndBrowserOffline);
};
